import React, { useEffect, useState } from 'react';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Loader from '../../components/Loader';

const EMPTY_FORM = { name: '', slug: '', chainId: '', symbol: '' };

// A slug is the URL-safe id used in the RPC endpoint, e.g. .../rpc/bnb-mainnet/<apiKey>.
const toSlug = (name) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function Networks() {
  const [networks, setNetworks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    apiCall(client.get('/network/admin/get_list'))
      .then(setNetworks)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Auto-derive the slug from the name until the user edits it manually.
  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => ({ ...f, name, slug: slugTouched ? f.slug : toSlug(name) }));
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: e.target.value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.post('/network/admin/create', { ...form, chainId: +form.chainId }));
      setForm(EMPTY_FORM);
      setShowAdd(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.put('/network/admin/update', { ...editing, chainId: +editing.chainId }));
      setEditing(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    try {
      await apiCall(client.patch('/network/admin/update_status', { uniqueId: row.uniqueId, status: !row.status }));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'slug', label: 'Slug', render: (row) => <code>{row.slug}</code> },
    { key: 'chainId', label: 'Chain ID' },
    { key: 'symbol', label: 'Symbol' },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} /> },
    {
      key: 'actions', label: '', render: (row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setEditing(row)}>Edit</button>
          <button className="btn btn-outline btn-sm" onClick={() => toggleStatus(row)}>
            {row.status ? 'Disable' : 'Enable'}
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="page-header-row">
        <h1>Networks</h1>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setSlugTouched(false); setShowAdd(true); }}>+ Add Network</button>
      </div>
      {error && <div className="form-error">{error}</div>}

      <div className="card">
        {loading ? (
          <Loader label="Loading networks..." />
        ) : (
          <DataTable columns={columns} rows={networks} emptyMessage="No networks configured yet." />
        )}
      </div>

      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Add network</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group"><label>Name</label>
                <input name="name" required value={form.name} onChange={handleNameChange} placeholder="BNB Smart Chain" /></div>
              <div className="form-group">
                <label>Slug</label>
                <input name="slug" required value={form.slug} onChange={handleSlugChange} placeholder="bnb-mainnet" />
                <p className="form-hint">
                  {/* URL-safe id used in the RPC endpoint (e.g. .../rpc/<strong>bnb-mainnet</strong>/&lt;apiKey&gt;).
                  Auto-filled from the name — edit freely,  */}
                  e.g. eth-mainnet, polygon-mainnet, bnb-mainnet.
                </p>
              </div>
              <div className="form-group"><label>Chain ID</label>
                <input name="chainId" type="number" required value={form.chainId} onChange={handleChange} placeholder="56" /></div>
              <div className="form-group" style={{ marginBottom: 20 }}><label>Symbol</label>
                <input name="symbol" required value={form.symbol} onChange={handleChange} placeholder="BNB" /></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add'}</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Edit network</h3>
            <form onSubmit={handleUpdate}>
              <div className="form-group"><label>Name</label>
                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div className="form-group">
                <label>Slug</label>
                <input required value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
                <p className="form-hint">URL-safe id used in the RPC endpoint URL — changing it will change existing endpoint URLs.</p>
              </div>
              <div className="form-group"><label>Chain ID</label>
                <input type="number" required value={editing.chainId} onChange={(e) => setEditing({ ...editing, chainId: e.target.value })} /></div>
              <div className="form-group" style={{ marginBottom: 20 }}><label>Symbol</label>
                <input required value={editing.symbol} onChange={(e) => setEditing({ ...editing, symbol: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>Save</button>
                <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
